// src/pages/SearchPage.js — exact HTML design
import React, { useState, useEffect, useRef } from 'react';
import { searchAPI, booksAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { decrypt, decryptData } from '../utils/security';

const FILTERS = [
  { key:'All',         label:'All',          color:'slate' },
  { key:'Commentary',  label:'Commentary',   color:'rose'  },
  { key:'Bare Act',    label:'Bare Act',     color:'slate' },
  { key:'Compilation', label:'Compilation',  color:'slate' },
];

// Recent searches stored in localStorage per user

const SUBJECT_ICONS = { Criminal:'⚖️', Constitutional:'🏛️', Civil:'📋', Evidence:'🔍', Corporate:'🏢', Arbitration:'🤝', Property:'🏠', Labour:'👷', Tax:'💰', Family:'👨‍👩‍👧', Environmental:'🌱' };
const SUBJECT_COLORS = ['#FAE8E6','#EEF2F8','#EBF4F0','#F7F0E6','#F0EDF8','#FAE8E6','#FBF4E8','#F9EDEC'];

const SearchPage = ({ onOpenBook }) => {
  const [query, setQuery]         = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults]     = useState({ books:[], chapters:[] });
  const [loading, setLoading]     = useState(false);
  const [activeFilter, setFilter] = useState('All');
  const [subjects, setSubjects]   = useState([]);
  const [recent, setRecent]       = useState(() => {
    try { return JSON.parse(localStorage.getItem('air_recent_searches') || '[]'); }
    catch { return []; }
  });
  const { getToken } = useApp();
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await booksAPI.getFilters();
        const topics = res.data?.topics || res.data?.categories || [];
        setSubjects(topics.slice(0, 8).map((t, i) => ({
          label: t,
          icon: SUBJECT_ICONS[t] || '📖',
          color: SUBJECT_COLORS[i % SUBJECT_COLORS.length]
        })));
      } catch {}
    })();
  }, []);

  const doSearch = async (q = query) => {
    if (!q.trim()) return;
    setSubmitted(true); setLoading(true);
    // Save to recent searches (localStorage)
    setRecent(prev => {
      const next = [q, ...prev.filter(r => r !== q)].slice(0, 8);
      try { localStorage.setItem('air_recent_searches', JSON.stringify(next)); } catch {}
      return next;
    });
    try {
      const token = await getToken();
      const res = await searchAPI.search(token, { q, limit:30 });
      // Bulk decrypt all sensitive fields in the search results
      const decrypted = decryptData(res.data || { books:[], chapters:[] });

      // Frontend sorting — decrypt() se name nikalo aur sort karo
      const q2 = q.trim().toLowerCase();
      const w2 = q2.split(/\s+/).filter(w => w.length > 1);
      const score = (name) => {
        name = (name || '').toLowerCase();
        if (name === q2) return 0;
        if (name.startsWith(q2)) return 1;
        if (name.includes(q2)) return 2;
        const matched = w2.filter(w => name.includes(w)).length;
        if (matched === w2.length) return 3;
        if (matched > 0) return 4;
        return 5;
      };
      if (decrypted.books) {
        decrypted.books.sort((a, b) =>
          score(decrypt(a.book_name) || decrypt(a.book_title)) -
          score(decrypt(b.book_name) || decrypt(b.book_title))
        );
      }
      if (decrypted.chapters) {
        decrypted.chapters.sort((a, b) =>
          score(decrypt(a.chapter_title)) - score(decrypt(b.chapter_title))
        );
      }

      setResults(decrypted);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };


  const filteredBooks = activeFilter === 'All' ? results.books
    : results.books.filter(b => {
        const n = b.category?.name?.toLowerCase() || '';
        if (activeFilter === 'Commentary')  return n.includes('comment') || n.includes('study');
        if (activeFilter === 'Bare Act')    return n.includes('bare') || n.includes('text');
        if (activeFilter === 'Compilation') return n.includes('compil') || n.includes('ebook');
        return true;
      });

  const total = results.books.length + results.chapters.length;

  return (
    <div style={{ background:'var(--off-white)', minHeight:'100vh' }}>

      {/* ── Top search bar (rose-pale bg like HTML) ── */}
      <div className="flex items-center gap-2 px-4 py-3 border-b"
        style={{ background:'var(--rose-pale)', borderColor:'var(--rose-lt)' }}>
        {submitted && (
          <button onClick={() => { setSubmitted(false); setQuery(''); setResults({books:[],chapters:[]}); }}
            style={{ color:'var(--rose)', fontSize:16 }}>←</button>
        )}
        <form className="flex-1 flex gap-2" onSubmit={e => { e.preventDefault(); doSearch(); }}>
          <div className="flex-1 flex items-center gap-2 bg-white rounded-lg px-3 py-2 border"
            style={{ borderColor:'var(--rose-lt)' }}>
            <span style={{ color:'var(--ink-faint)', fontSize:14 }}>🔍</span>
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search commentaries, bare acts…"
              className="flex-1 outline-none bg-transparent"
              style={{ fontSize:12, color:'var(--ink)', fontFamily:'Nunito,sans-serif' }}
              autoFocus/>
            {query && (
              <button type="button" onClick={() => { setQuery(''); setSubmitted(false); setResults({books:[],chapters:[]}); }}
                style={{ color:'var(--ink-lt)', fontSize:13 }}>✕</button>
            )}
          </div>
          <button type="submit"
            className="px-4 py-2 text-white rounded-lg font-bold border-0 cursor-pointer"
            style={{ background:'var(--rose)', fontSize:12, fontFamily:'Nunito,sans-serif' }}>Search</button>
        </form>
      </div>

      {/* ── Results meta + filters ── */}
      {submitted && (
        <>
          <div className="flex justify-between items-center px-4 py-2 bg-white border-b"
            style={{ borderColor:'var(--border)', fontSize:10, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>
            <span><b style={{ color:'var(--ink)' }}>{total} results</b> across {results.books.length} titles</span>
            <span style={{ color:'var(--rose)', fontWeight:700, fontSize:9 }}>Relevance ▾</span>
          </div>
          {/* Filter chips */}
          <div className="flex gap-2 px-4 py-2 border-b overflow-x-auto no-scrollbar"
            style={{ background:'var(--off-white)', borderColor:'var(--border)' }}>
            {FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className="flex-shrink-0 px-3 py-1 rounded-full font-bold cursor-pointer border-0"
                style={{
                  fontSize:10, fontFamily:'Nunito,sans-serif',
                  background: activeFilter === f.key ? 'var(--slate)' : 'white',
                  color:      activeFilter === f.key ? 'white'        : 'var(--ink-mid)',
                  border:     activeFilter === f.key ? 'none'         : '1px solid var(--border)',
                }}>
                {f.label} {activeFilter === f.key && `(${total})`}
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div className="flex items-center justify-center py-16 gap-3">
          <div className="rounded-full border-t-2 animate-spin"
            style={{ width:24, height:24, border:'2px solid var(--border)', borderTopColor:'var(--rose)' }}/>
          <span style={{ fontSize:12, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>Searching…</span>
        </div>
      )}

      {/* ── Before search ── */}
      {!submitted && !loading && (
        <div className="px-4 py-4 animate-fade-in">
          <div className="font-bold uppercase mb-3" style={{ fontSize:9, color:'var(--ink-lt)', letterSpacing:2, fontFamily:'Nunito,sans-serif' }}>
            Recent Searches
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            {recent.map(r => (
              <button key={r} onClick={() => { setQuery(r); doSearch(r); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border rounded-lg transition-all hover:border-rose-lt"
                style={{ fontSize:11, color:'var(--ink-mid)', borderColor:'var(--border)', fontFamily:'Nunito,sans-serif' }}>
                <span style={{ color:'var(--ink-faint)' }}>⏱</span>{r}
              </button>
            ))}
          </div>
          <div className="font-bold uppercase mb-3" style={{ fontSize:9, color:'var(--ink-lt)', letterSpacing:2, fontFamily:'Nunito,sans-serif' }}>
            Browse by Subject
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {subjects.map(cat => (
              <button key={cat.label} onClick={() => { setQuery(cat.label); doSearch(cat.label); }}
                className="flex items-center gap-3 p-3 rounded-xl border bg-white hover:shadow-sm hover:border-rose-lt transition-all text-left"
                style={{ borderColor:'var(--border)' }}>
                <div className="flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ width:40, height:40, background:cat.color, fontSize:20 }}>{cat.icon}</div>
                <div className="font-bold" style={{ fontSize:12, color:'var(--ink)', fontFamily:'Nunito,sans-serif' }}>{cat.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Results ── */}
      {submitted && !loading && (
        <div className="animate-fade-in">
          {filteredBooks.length === 0 && results.chapters.length === 0 ? (
            <div className="text-center py-16">
              <div style={{ fontSize:40, marginBottom:12, opacity:.3 }}>🔍</div>
              <div className="font-display font-bold mb-2" style={{ fontSize:18, color:'var(--ink)', fontFamily:'Playfair Display,serif' }}>No results</div>
              <div style={{ fontSize:13, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>Try different keywords</div>
            </div>
          ) : (
            <>
              {/* Book results */}
              {filteredBooks.map((book, i) => (
                <div key={book._id} className="px-4 py-3 border-b cursor-pointer transition-colors"
                  style={{ borderColor:'var(--border-lt)', background:'white' }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--rose-pale)'}
                  onMouseLeave={e => e.currentTarget.style.background='white'}
                  onClick={() => onOpenBook(book)}>
                  {/* Source label */}
                  <div className="font-bold uppercase mb-0.5"
                    style={{ fontSize:9, color:'var(--rose)', letterSpacing:.5, fontFamily:'Nunito,sans-serif' }}>
                    {book.category?.name || 'Book'} {(book.authors||[]).length > 0 ? `· ${book.authors[0].name}` : ''}
                  </div>
                  {/* Chapter/title */}
                  <div className="font-display font-semibold mb-1"
                    style={{ fontSize:13, color:'var(--ink)', fontFamily:'Playfair Display,serif' }}>
                    {decrypt(book.book_name || book.book_title)}
                  </div>
                  {/* Snippet with highlight */}
                  {book.book_short_description && (
                    <div style={{ fontSize:10, color:'var(--ink-lt)', lineHeight:1.5, fontFamily:'Nunito,sans-serif' }}
                      dangerouslySetInnerHTML={{ __html: decrypt(book.book_short_description).replace(/<[^>]*>/g,'').substring(0,120) + '…' }}/>
                  )}
                  {/* Section tag + Jump button */}
                  <div className="flex items-center justify-between mt-1.5">
                    {book.state && (
                      <span className="rounded font-bold"
                        style={{ background:'var(--slate-pale)', color:'var(--slate)', fontSize:9, padding:'2px 6px', fontFamily:'Nunito,sans-serif' }}>
                        {book.state}
                      </span>
                    )}
                    <button className="float-right text-white rounded font-bold border-0 cursor-pointer"
                      style={{ background:'var(--rose)', fontSize:9, padding:'4px 8px', fontFamily:'Nunito,sans-serif' }}
                      onClick={e => { e.stopPropagation(); onOpenBook(book); }}>
                      Jump →
                    </button>
                  </div>
                </div>
              ))}

              {/* Chapter results */}
              {results.chapters.length > 0 && (
                <>
                  <div className="px-4 py-2 font-bold uppercase border-b"
                    style={{ fontSize:9, color:'var(--ink-lt)', letterSpacing:2, fontFamily:'Nunito,sans-serif', borderColor:'var(--border)', background:'var(--off-white)' }}>
                    Found in Chapters
                  </div>
                  {results.chapters.map((ch, i) => (
                    <div key={`${ch._id}-${ch.chapter_id}`}
                      className="px-4 py-3 border-b cursor-pointer transition-colors"
                      style={{ borderColor:'var(--border-lt)', background:'white' }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--rose-pale)'}
                      onMouseLeave={e => e.currentTarget.style.background='white'}
                      onClick={() => onOpenBook({ _id: ch.book_id, book_name: ch.book_name, chapter_id: ch.chapter_id })}>
                      <div className="font-bold uppercase mb-0.5"
                        style={{ fontSize:9, color:'var(--rose)', letterSpacing:.5, fontFamily:'Nunito,sans-serif' }}>
                        {decrypt(ch.book_name)}
                      </div>
                      <div className="font-display font-semibold mb-1"
                        style={{ fontSize:13, color:'var(--ink)', fontFamily:'Playfair Display,serif' }}>
                        {decrypt(ch.chapter_title)}
                      </div>
                      <div style={{ fontSize:10, color:'var(--ink-lt)', lineHeight:1.5, fontStyle:'italic', fontFamily:'Lora,serif' }}>
                        "…{decrypt(ch.snippet)}…"
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="rounded font-bold"
                          style={{ background:'var(--slate-pale)', color:'var(--slate)', fontSize:9, padding:'2px 6px', fontFamily:'Nunito,sans-serif' }}>
                          § Chapter match
                        </span>
                        <button className="text-white rounded font-bold border-0 cursor-pointer"
                          style={{ background:'var(--rose)', fontSize:9, padding:'4px 8px', fontFamily:'Nunito,sans-serif' }}>
                          Jump →
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchPage;